import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import { ArrowLeft, ArrowRight } from '@common/components/Icons';
import useIsomorphicLayoutEffect from '@common/hooks/useIsomorphicLayoutEffect';
import { useDevice } from '@common/hooks/useDevice';

import {
  Container,
  PaginationButton,
  PageInput,
  TotalPages,
  HiddenSubmit,
} from './Pagination.styled';

const usePageSubmit = (inputRef, onPageChange, page) => {
  const { isMobile } = useDevice();
  const isVirtualKeyboardVisible = useRef(false);

  useEffect(() => {
    const isInputFocused = inputRef.current === document.activeElement;

    const onKeyDown = e => {
      if (e.key === 'Enter' && isInputFocused) {
        e.preventDefault();
        e.stopPropagation();

        onPageChange(page);
      }
    };

    if (!isMobile) {
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [inputRef, isMobile, onPageChange, page]);

  useEffect(() => {
    const onVirtualKeyboardVisibilityChange = event => {
      if (event.target.height === window.innerHeight) {
        if (isVirtualKeyboardVisible.current) {
          onPageChange(page);
        }
        isVirtualKeyboardVisible.current = false;
      } else {
        isVirtualKeyboardVisible.current = true;
      }
    };

    if (isMobile) {
      if ('visualViewport' in window) {
        window.visualViewport.addEventListener(
          'resize',
          onVirtualKeyboardVisibilityChange,
        );
      }
    }

    return () => {
      window.visualViewport.removeEventListener(
        'resize',
        onVirtualKeyboardVisibilityChange,
      );
    };
  }, [isMobile, onPageChange, page, isVirtualKeyboardVisible]);
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [page, setPage] = useState(currentPage);

  usePageSubmit(inputRef, onPageChange, page);

  useIsomorphicLayoutEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleChange = e => {
    const newPage = parseInt(e.target.value, 10);

    if (typeof newPage !== 'number') {
      return;
    }

    const minMaxNewPage = Math.max(Math.min(newPage, totalPages), 1);

    setPage(minMaxNewPage);
  };

  return (
    <Container>
      <PaginationButton
        icon={<ArrowLeft />}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      />

      <PageInput
        type="number"
        min="1"
        max={totalPages}
        inputMode="numeric"
        value={page}
        onClick={e => e.target.select()}
        onChange={handleChange}
        ref={inputRef}
      />
      <HiddenSubmit type="submit">submit</HiddenSubmit>

      <TotalPages>
        {t('pagination.ofTotalPages', 'von')}
        &nbsp;
        {totalPages}
      </TotalPages>

      <PaginationButton
        icon={<ArrowRight />}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      />
    </Container>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default Pagination;
